// SPDX-License-Identifier: AGPL-3.0-or-later

import {sampleServerTime} from '@app/features/voice_scene/net/RoomServerClock';
import {SERVER_MUTE_ATTRIBUTE} from '@app/features/voice_scene/net/RoomSignals';
import {decodeVoiceWatch, VOICE_WATCH_ATTRIBUTE, type VoiceWatch} from '@fluxer/constants/src/VoiceRoomWatch';
import {type Participant, type Room, RoomEvent} from 'livekit-client';
import {useEffect, useState} from 'react';

const NO_WATCHES: ReadonlyMap<string, VoiceWatch> = new Map();

function participants(room: Room): Array<Participant> {
	return [room.localParticipant, ...room.remoteParticipants.values()];
}

function readRaw(room: Room, sinceMs: number | null): Map<string, string> {
	const raw = new Map<string, string>();
	for (const participant of participants(room)) {
		if (participant.attributes[SERVER_MUTE_ATTRIBUTE] === 'true') continue;
		const value = participant.attributes[VOICE_WATCH_ATTRIBUTE];
		const watch = decodeVoiceWatch(value);
		if (watch && (sinceMs === null || watch.writtenAtMs >= sinceMs)) raw.set(participant.identity, value!);
	}
	return raw;
}

const sameRaw = (a: ReadonlyMap<string, string>, b: ReadonlyMap<string, string>): boolean =>
	a.size === b.size && [...a].every(([identity, value]) => b.get(identity) === value);

// Identity → the video that participant plays on the room TV, from the attribute the server writes. A late joiner gets
// every attribute on connect; the opener leaving takes theirs along. Other attribute changes keep the same map.
// sinceMs (watch_since): a video written before the switch last came on is stale. A value that changes for someone
// already here sets the server clock (RoomServerClock); one read on arrival may be old and does not. A server-muted
// participant plays none, also one their mute's own write did not clear.
export function useRoomWatches(room: Room | null, sinceMs: number | null = null): ReadonlyMap<string, VoiceWatch> {
	const [watches, setWatches] = useState(NO_WATCHES);
	useEffect(() => {
		if (!room) {
			setWatches(NO_WATCHES);
			return;
		}
		let raw: ReadonlyMap<string, string> | null = null;
		let known = new Set<string>();
		const refresh = () => {
			const next = readRaw(room, sinceMs);
			known = new Set(participants(room).map((participant) => participant.identity));
			if (raw && sameRaw(raw, next)) return;
			raw = next;
			setWatches(
				next.size === 0
					? NO_WATCHES
					: new Map([...next].map(([identity, value]) => [identity, decodeVoiceWatch(value)!])),
			);
		};
		const onAttributes = (changed: Record<string, string>, participant: Participant) => {
			const value = participant.attributes[VOICE_WATCH_ATTRIBUTE];
			if (
				known.has(participant.identity) &&
				VOICE_WATCH_ATTRIBUTE in changed &&
				value !== raw?.get(participant.identity)
			) {
				const watch = decodeVoiceWatch(value);
				if (watch && (sinceMs === null || watch.writtenAtMs >= sinceMs)) sampleServerTime(watch.writtenAtMs);
			}
			refresh();
		};
		refresh();
		room.on(RoomEvent.ParticipantAttributesChanged, onAttributes);
		room.on(RoomEvent.ParticipantConnected, refresh);
		room.on(RoomEvent.ParticipantDisconnected, refresh);
		return () => {
			room.off(RoomEvent.ParticipantAttributesChanged, onAttributes);
			room.off(RoomEvent.ParticipantConnected, refresh);
			room.off(RoomEvent.ParticipantDisconnected, refresh);
		};
	}, [room, sinceMs]);
	return watches;
}
